/**
 * Auto-detect edge-to-edge logo artwork and pad it so the neutral
 * surround stays visible when the image is rendered on light or dark
 * surfaces.
 *
 * Loads the uploaded data URL into an Image, paints it to a canvas,
 * and inspects the 4 edge strips. If more than 8% of edge pixels have
 * alpha > 200 (meaning the artwork bleeds off the canvas), we redraw
 * onto a larger transparent canvas with ~12% padding.
 *
 * Formats without an alpha channel (jpeg, jpg) never trip the detector
 * — they're treated as fully opaque and always padded.
 */
export async function autoPadLogo(dataUrl, {
  edgeAlphaThreshold = 200,
  edgeFillFractionTrigger = 0.08,
  paddingRatio = 0.12,
} = {}) {
  if (!dataUrl || typeof dataUrl !== "string") return dataUrl;
  const mime = /^data:(image\/[a-z+.-]+);/i.exec(dataUrl)?.[1] || "";
  const isJpeg = /jpe?g/i.test(mime);

  const img = await _loadImage(dataUrl);
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  if (!w || !h) return dataUrl;

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);

  let bleeds = isJpeg;
  if (!bleeds) {
    try {
      bleeds = _edgeBleeds(ctx, w, h, edgeAlphaThreshold, edgeFillFractionTrigger);
    } catch {
      // Cross-origin or getImageData failure — leave the logo alone.
      return dataUrl;
    }
  }
  if (!bleeds) return dataUrl;

  const pad = Math.round(Math.max(w, h) * paddingRatio);
  const outW = w + pad * 2;
  const outH = h + pad * 2;
  const out = document.createElement("canvas");
  out.width = outW;
  out.height = outH;
  const octx = out.getContext("2d");
  // Transparent surround so the parent's ``bg-muted`` (or dark surface)
  // shows through — no baked-in white/orange background.
  octx.clearRect(0, 0, outW, outH);
  octx.drawImage(img, pad, pad, w, h);
  // PNG preserves the transparent border. JPEG would flatten it, so we
  // always output PNG regardless of the source mime.
  return out.toDataURL("image/png");
}

function _loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function _edgeBleeds(ctx, w, h, alphaMin, fracTrigger) {
  // Grab the 4 edge strips (1px tall/wide) and count opaque pixels.
  const strips = [
    ctx.getImageData(0, 0, w, 1),       // top
    ctx.getImageData(0, h - 1, w, 1),   // bottom
    ctx.getImageData(0, 0, 1, h),       // left
    ctx.getImageData(w - 1, 0, 1, h),   // right
  ];
  let opaque = 0;
  let total = 0;
  for (const s of strips) {
    for (let i = 3; i < s.data.length; i += 4) {
      total += 1;
      if (s.data[i] > alphaMin) opaque += 1;
    }
  }
  return total > 0 && opaque / total > fracTrigger;
}
